import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface CheckoutModalProps {
  clientSecret: string;
  planName: string;
  monthlyPrice: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function CheckoutForm({ onSuccess, onCancel, planName, monthlyPrice }: Omit<CheckoutModalProps, 'clientSecret'>) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message || '決済に失敗しました。');
      setLoading(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="checkout-modal__info">
        <p className="checkout-modal__plan">{planName}</p>
        <p className="checkout-modal__price">月額 {monthlyPrice}円（税込）</p>
        <p className="checkout-modal__trial">端末到着後30日間無料トライアル</p>
      </div>

      <PaymentElement />

      {error && <p className="checkout-modal__error">{error}</p>}

      <div className="checkout-modal__actions">
        <button
          type="submit"
          className="checkout-modal__submit"
          disabled={!stripe || loading}
        >
          {loading ? '処理中...' : 'お申し込みを確定する'}
        </button>
        <button
          type="button"
          className="checkout-modal__cancel"
          onClick={onCancel}
          disabled={loading}
        >
          戻る
        </button>
      </div>
    </form>
  );
}

export default function CheckoutModal({ clientSecret, planName, monthlyPrice, onSuccess, onCancel }: CheckoutModalProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className={`checkout-overlay${visible ? ' checkout-overlay--visible' : ''}`} onClick={onCancel}>
      <div className={`checkout-sheet${visible ? ' checkout-sheet--visible' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="checkout-sheet__handle" />
        <h3 className="checkout-sheet__title">お支払い情報の入力</h3>

        <Elements stripe={stripePromise} options={{ clientSecret, locale: 'ja' }}>
          <CheckoutForm
            planName={planName}
            monthlyPrice={monthlyPrice}
            onSuccess={onSuccess}
            onCancel={onCancel}
          />
        </Elements>
      </div>
    </div>
  );
}
